import { Component } from '@theme/component';

class NeobrutalistIntroduction extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#initLetterWrapping();
    this.#initVideoControls();
    this.#initEmojiBurst();
  }

  // Wrap letters of Welcome Heading to animate them individually on hover
  #initLetterWrapping() {
    const headings = this.querySelectorAll('.neobrutalist-intro__heading-animated');
    headings.forEach((heading) => {
      const text = heading.textContent.trim();
      heading.textContent = '';

      [...text].forEach((char) => {
        const span = document.createElement('span');
        span.className = 'neobrutalist-intro__letter';

        if (char === ' ') {
          span.innerHTML = '&nbsp;';
        } else {
          span.textContent = char;
        }

        heading.appendChild(span);
      });
    });
  }

  // Handle play button overlay and video execution
  #initVideoControls() {
    const videoWrapper = this.querySelector('.neobrutalist-intro__video-wrapper');
    if (!videoWrapper) return;

    const playBtn = videoWrapper.querySelector('.neobrutalist-intro__play-btn');
    const nativeVideo = videoWrapper.querySelector('video');
    const iframePlaceholder = videoWrapper.querySelector('.neobrutalist-intro__iframe-placeholder');

    if (playBtn) {
      playBtn.addEventListener('click', (e) => {
        e.preventDefault();

        videoWrapper.classList.add('video-playing');
        this.#spawnBurst(playBtn);

        if (nativeVideo) {
          nativeVideo.play();
          nativeVideo.controls = true;
        } else if (iframePlaceholder) {
          const embedUrl = iframePlaceholder.getAttribute('data-embed-url');
          if (embedUrl) {
            const iframe = document.createElement('iframe');
            iframe.src = embedUrl + (embedUrl.includes('?') ? '&' : '?') + 'autoplay=1';
            iframe.setAttribute('frameborder', '0');
            iframe.setAttribute('allow', 'autoplay; encrypted-media');
            iframe.setAttribute('allowfullscreen', 'true');
            iframe.className = 'neobrutalist-intro__video-iframe';

            iframePlaceholder.textContent = '';
            iframePlaceholder.appendChild(iframe);
          }
        }
      });
    }
  }

  // Bind clicks on mascot/sticker to fire emoji particle blasts
  #initEmojiBurst() {
    const sticker = this.querySelector('.neobrutalist-intro__sticker');
    if (sticker) {
      sticker.addEventListener('click', (e) => {
        e.preventDefault();
        this.#spawnBurst(sticker);

        // Wobble the video card briefly for a connected feedback loop
        const videoCard = this.querySelector('.neobrutalist-intro__video-card');
        if (videoCard) {
          videoCard.classList.add('neobrutalist-intro__video-card--wobble');
          videoCard.addEventListener('animationend', () => {
            videoCard.classList.remove('neobrutalist-intro__video-card--wobble');
          }, { once: true });
        }
      });
    }
  }

  // Particle generation helper
  #spawnBurst(targetEl) {
    const rect = targetEl.getBoundingClientRect();
    const burstX = rect.left + rect.width / 2 + window.scrollX;
    const burstY = rect.top + rect.height / 2 + window.scrollY;

    const svgFlame = `<svg viewBox="0 0 24 24" fill="#F20544" stroke="#000" stroke-width="2" style="width:1em;height:1em;"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"></path></svg>`;
    const svgSparkle = `<svg viewBox="0 0 24 24" fill="#04B2D9" stroke="#000" stroke-width="2" style="width:1em;height:1em;"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path></svg>`;
    const svgStar = `<svg viewBox="0 0 24 24" fill="#F2CB05" stroke="#000" stroke-width="2" style="width:1em;height:1em;"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>`;
    const svgHeart = `<svg viewBox="0 0 24 24" fill="#FF007F" stroke="#000" stroke-width="2" style="width:1em;height:1em;"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>`;

    const icons = [svgFlame, svgSparkle, svgStar, svgHeart, svgSparkle, svgStar];
    const particleCount = 15;

    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div');
      particle.className = 'neobrutalist-intro__particle';
      particle.innerHTML = icons[Math.floor(Math.random() * icons.length)];

      // Starting coordinates
      particle.style.left = `${burstX}px`;
      particle.style.top = `${burstY}px`;

      // Randomized initial velocities & directions
      const angle = Math.random() * Math.PI * 2;
      const speed = 4 + Math.random() * 8;
      const vx = Math.cos(angle) * speed;
      const vy = Math.sin(angle) * speed;

      document.body.appendChild(particle);

      let x = burstX;
      let y = burstY;
      let opacity = 1;
      let scale = 1;

      const animate = () => {
        x += vx;
        y += vy + 0.15; // add custom gravity
        opacity -= 0.025;
        scale -= 0.015;

        particle.style.left = `${x}px`;
        particle.style.top = `${y}px`;
        particle.style.opacity = opacity;
        particle.style.transform = `translate(-50%, -50%) scale(${Math.max(0, scale)})`;

        if (opacity > 0) {
          requestAnimationFrame(animate);
        } else {
          particle.remove();
        }
      };

      requestAnimationFrame(animate);
    }
  }
}

if (!customElements.get('neobrutalist-introduction')) {
  customElements.define('neobrutalist-introduction', NeobrutalistIntroduction);
}
